import { ButtonList } from '@dixa/lib-shared-habla';
import { sizes } from '@dixa/lib-shared-tokens';
import * as Popover from '@radix-ui/react-popover';
import { memo, useCallback, useRef, useState } from 'react';

import type { SidebarFooterAction } from '../../sidebarFooter';
import { SidebarFooterButton } from '../button/sidebarFooterButton';

import styles from './sidebarFooterPopover.module.scss';

const HOVER_DELAY_MS = 300;

type SidebarFooterPopoverProps = {
  actions: SidebarFooterAction[];
};

export const SidebarFooterPopover = memo(function SidebarFooterPopover({
  actions,
}: SidebarFooterPopoverProps): JSX.Element {
  const [isOpen, setIsOpen] = useState(false);
  const openTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleMouseEnter = useCallback(() => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
    openTimer.current = setTimeout(() => setIsOpen(true), HOVER_DELAY_MS);
  }, []);

  const handleMouseLeave = useCallback(() => {
    if (openTimer.current) {
      clearTimeout(openTimer.current);
      openTimer.current = null;
    }
    closeTimer.current = setTimeout(() => setIsOpen(false), HOVER_DELAY_MS);
  }, []);

  return (
    <Popover.Root open={isOpen}>
      <Popover.Trigger
        asChild
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onClick={(e) => e.preventDefault()}
      >
        {/*
         * In collapsed mode this button has the "beamer-button" class so the
         * Beamer notification badge is rendered on it, but data-beamer-click="false"
         * prevents it from opening Beamer directly — the actual Beamer trigger
         * is inside the popover's action list.
         */}
        <SidebarFooterButton
          icon="dots-h"
          className="beamer-button"
          data-beamer-click="false"
        />
      </Popover.Trigger>

      <Popover.Portal>
        <Popover.Content
          className={styles.popover}
          side="right"
          align="end"
          sideOffset={parseInt(sizes.spacer.values[100])}
          onOpenAutoFocus={(e) => e.preventDefault()}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          <ButtonList>
            {actions.map((action) => (
              <ButtonList.Item
                className={action.className}
                key={action.icon}
                icon={action.icon}
                iconProps={{ size: 20 }}
                onClick={() => {
                  action.onClick();
                  setIsOpen(false);
                }}
              >
                {action.label}
              </ButtonList.Item>
            ))}
          </ButtonList>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
});
